import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { isAxiosError } from 'axios';
import {
  listDeployDrafts,
  approveDeployDraft,
  rejectDeployDraft,
} from '../api/deploy-drafts.api.ts';
import { maintenanceKeys } from '@/features/maintenances/index.ts';
import { deployDraftKeys } from '@/shared/hooks/useDeployDraftsCount.ts';
import type { DeployDraftListParams } from '@/shared/types/deploy.ts';
import type { ApproveDeployDraftDTO } from '../api/deploy-drafts.api.ts';

export { deployDraftKeys };

export function useDeployDraftList(params: DeployDraftListParams, enabled = true) {
  return useQuery({
    queryKey: deployDraftKeys.list(params),
    queryFn: () => listDeployDrafts(params),
    enabled,
  });
}

function handleReviewError(err: unknown, fallback: string) {
  if (isAxiosError(err) && (err.response?.status === 409 || err.response?.status === 422)) {
    toast.error(err.response.data?.error ?? fallback);
    return;
  }
  toast.error(fallback);
}

export function useApproveDeployDraft(onSuccess: () => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: ApproveDeployDraftDTO }) =>
      approveDeployDraft(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: deployDraftKeys.all });
      // A aprovação cria uma manutenção — a lista de manutenções fica velha.
      qc.invalidateQueries({ queryKey: maintenanceKeys.all });
      toast.success('Deploy aprovado e manutenção criada');
      onSuccess();
    },
    onError: (err) => handleReviewError(err, 'Erro ao aprovar deploy'),
  });
}

export function useRejectDeployDraft(onSuccess: () => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, motivo }: { id: string; motivo: string }) => rejectDeployDraft(id, motivo),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: deployDraftKeys.all });
      toast.success('Deploy rejeitado');
      onSuccess();
    },
    onError: (err) => handleReviewError(err, 'Erro ao rejeitar deploy'),
  });
}
